import { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FilePenLine,
  Pencil,
  Printer,
  Send,
  Trash2,
  XCircle,
} from 'lucide-react';

import {
  cancelVoucher,
  deleteDraftVoucher,
  printVoucher,
  submitVoucher,
  updateVoucherRemark,
} from '#/api/fmsMgmt/voucher';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import FmsDocumentCommandBarActions from '#/modules/finMgmt/shared/components/FmsDocumentCommandBarActions';
import FmsRemarkDialog from '#/modules/finMgmt/shared/components/FmsRemarkDialog';
import useFmsPrintPdf from '#/modules/finMgmt/shared/hooks/useFmsPrintPdf';
import DocumentRejectReasonDialog from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import { useCan } from '#/modules/infra/auth/useCan';
import {
  voucherDetailQueryKey,
  voucherListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

import {
  getVoucherApiErrorMessage,
  isVoucherVersionConflict,
} from '../helper/apiError';
import {
  canCancelVoucher,
  canDeleteVoucher,
  canEditVoucher,
  canSubmitVoucher,
} from '../helper/voucherStatus';

/**
 * personal：個人中心 — 編輯、提交、刪除草稿。
 * finance：財務管理 — 作廢。改備註兩邊都有（依權限）。
 */
const EXPOSED_ORDER = {
  personal: ['edit', 'submit', 'print'],
  finance: ['cancel', 'print'],
};

const ACTION_GROUPS = [
  { key: 'content', label: '憑證內容', ids: ['edit', 'editRemark', 'print'] },
  { key: 'status', label: '憑證狀態', ids: ['submit', 'cancel', 'deleteDraft'] },
];

const PERSONAL_ONLY = new Set(['edit', 'submit', 'deleteDraft']);

function pickUpdatedAt(detail) {
  return detail?.record?.updatedAt ?? detail?.updatedAt ?? null;
}

const VoucherCommandBar = ({ detail, mode }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, confirmDialog } = useConfirmDialog();
  const canUpdate = useCan(PERM.FMS_VOUCHER_UPDATE);
  const canDelete = useCan(PERM.FMS_VOUCHER_DELETE);
  const canSubmit = useCan(PERM.FMS_VOUCHER_SUBMIT);
  const canCancel = useCan(PERM.FMS_VOUCHER_CANCEL);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [remarkOpen, setRemarkOpen] = useState(false);

  const id = detail?.id;
  const status = detail?.status;
  const backTo = mode === 'finance' ? ROUTES.fmsVoucher : ROUTES.personalCenterVouchersPanel;

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: voucherDetailQueryKey(id) });
    await queryClient.invalidateQueries({ queryKey: voucherListQueryKeyPrefix() });
  }

  const commandMutation = useMutation({
    mutationFn: ({ action, payload }) => {
      if (action === 'submit') {
        return submitVoucher(id);
      }
      if (action === 'cancel') {
        return cancelVoucher(id, { cancelReason: payload.cancelReason });
      }
      if (action === 'deleteDraft') {
        return deleteDraftVoucher(id);
      }
      return updateVoucherRemark(id, {
        remark: payload.remark,
        updatedAt: pickUpdatedAt(detail),
      });
    },
    onSuccess: async (result, { action }) => {
      if (action === 'deleteDraft') {
        toast.success('已刪除憑證草稿');
        await queryClient.invalidateQueries({ queryKey: voucherListQueryKeyPrefix() });
        navigate(backTo);
        return;
      }
      toast.success(result?.message || '操作成功');
      setCancelOpen(false);
      setRemarkOpen(false);
      await refresh();
    },
    onError: (error) => {
      toast.error(getVoucherApiErrorMessage(error, '操作失敗'));
      if (isVoucherVersionConflict(error)) {
        refresh();
      }
    },
  });

  const pending = commandMutation.isPending;

  async function confirmThen(action, options) {
    const ok = await confirm(options);
    if (ok) {
      commandMutation.mutate({ action });
    }
  }

  const { isPrinting, print } = useFmsPrintPdf(
    () => printVoucher(id, { voucherNo: detail?.voucherNo }),
    getVoucherApiErrorMessage,
  );

  const allActions = {
    print: {
      id: 'print',
      label: isPrinting ? '列印中…' : '列印',
      icon: Printer,
      available: Boolean(id),
      disabled: isPrinting,
      showExposedIcon: true,
      exposedVariant: 'outline',
      onSelect: print,
    },
    edit: {
      id: 'edit',
      label: '編輯',
      icon: Pencil,
      to: ROUTES.personalCenterVoucherEdit(id),
      available: canUpdate && canEditVoucher(status),
      showExposedIcon: true,
      exposedClassName: 'document-detail-edit-action',
    },
    editRemark: {
      id: 'editRemark',
      label: '編輯備註',
      icon: FilePenLine,
      available: canUpdate,
      showExposedIcon: true,
      exposedVariant: 'outline',
      onSelect: () => setRemarkOpen(true),
    },
    submit: {
      id: 'submit',
      label: '提交',
      icon: Send,
      available: canSubmit && canSubmitVoucher(status),
      onSelect: () => confirmThen('submit', {
        title: '提交憑證',
        message: '提交後憑證生效，可被請款單引用，且不能再修改基本資料。確定提交？',
        confirmText: '提交',
      }),
    },
    cancel: {
      id: 'cancel',
      label: '作廢',
      icon: XCircle,
      available: canCancel && canCancelVoucher(status),
      destructive: true,
      onSelect: () => setCancelOpen(true),
    },
    deleteDraft: {
      id: 'deleteDraft',
      label: '刪除草稿',
      icon: Trash2,
      available: canDelete && canDeleteVoucher(status),
      destructive: true,
      onSelect: () => confirmThen('deleteDraft', {
        title: '刪除憑證草稿',
        message: '草稿刪除後無法復原；已上傳過附件的草稿無法刪除。確定刪除？',
        confirmText: '刪除',
        destructive: true,
      }),
    },
  };

  const actionMap = Object.fromEntries(
    Object.entries(allActions).map(([key, action]) => [
      key,
      mode === 'finance' && PERSONAL_ONLY.has(key) ? { ...action, available: false } : action,
    ]),
  );

  return (
    <>
      {confirmDialog}
      <FmsDocumentCommandBarActions
        actionMap={actionMap}
        actionGroups={ACTION_GROUPS}
        workflowExposedOrder={EXPOSED_ORDER[mode]}
        isPending={pending}
      />

      <DocumentRejectReasonDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="作廢憑證"
        description="已被請款單引用的憑證不能作廢。"
        confirmText="作廢"
        reasonLabel="作廢原因"
        emptyErrorMessage="請填寫作廢原因"
        isPending={pending}
        onConfirm={(cancelReason) => {
          commandMutation.mutate({ action: 'cancel', payload: { cancelReason } });
        }}
      />
      <FmsRemarkDialog
        open={remarkOpen}
        onOpenChange={setRemarkOpen}
        initialRemark={detail?.remark ?? ''}
        isPending={pending}
        inputId="voucherRemark"
        onSave={(remark) => commandMutation.mutate({ action: 'remark', payload: { remark } })}
      />
    </>
  );
};

VoucherCommandBar.propTypes = {
  detail: PropTypes.object.isRequired,
  mode: PropTypes.oneOf(['personal', 'finance']).isRequired,
};

export default VoucherCommandBar;
